import * as Sentry from "@sentry/nextjs";
import { CRITICAL_LOG_KEYS } from "@/lib/fhir/reference";

/**
 * Extracts domain IDs from a payload object and sets them as `vibrant.*`
 * Sentry tags on the current scope.
 *
 * Only sets tags for keys that exist as non-empty strings in the payload.
 * Searches one level deep (i.e. also checks `payload.context.*`) to handle
 * payloads that nest IDs under a `context` sub-object.
 */
export function setDomainSentryTags(payload: unknown): void {
  if (!payload || typeof payload !== "object") return;

  const record = payload as Record<string, unknown>;
  const tags: Record<string, string> = {};

  for (const key of CRITICAL_LOG_KEYS) {
    const value = record[key];
    if (typeof value === "string" && value.length > 0) {
      tags[`vibrant.${key}`] = value;
    }
  }

  // Also check a nested `context` object (some server actions nest IDs there)
  const context = record.context;
  if (context && typeof context === "object") {
    const contextRecord = context as Record<string, unknown>;
    for (const key of CRITICAL_LOG_KEYS) {
      if (tags[`vibrant.${key}`]) continue; // top-level wins
      const value = contextRecord[key];
      if (typeof value === "string" && value.length > 0) {
        tags[`vibrant.${key}`] = value;
      }
    }
  }

  if (Object.keys(tags).length > 0) {
    Sentry.getCurrentScope().setTags(tags);
  }
}
